// Runs synchronously in the head, before first paint (layout.rs loads it plain, not as a
// module, for exactly that reason). Four things, none of which may wait for main.js:
// 1. the js class: pages the player will take over hide their server-rendered turns only
//    when js is here to replay them;
// 2. the viewer's look, show and animations, so nothing flashes before their choice applies;
// 3. broadcast isolation: detects ringside/kiosk mode, persists isolation state, and suppresses
//    all telemetry and banner rendering;
// 4. google consent mode v2 and the tag container, when the html element names one (BASHO_GTM_ID)
//    and broadcast mode is not active. The csp allows no inline script anywhere.
import { FONT_KEY, MOTION_KEY, onBroadcast, PANEL_KEY, SHOW_KEY, showAsked, THEME_KEY } from "./env.ts";

(function () {
  var h = document.documentElement;
  h.classList.add("js");
  try {
    if (window.localStorage) {
      var f = window.localStorage.getItem(FONT_KEY);
      if (f) h.dataset.font = f;
      // the show (show.ts): the stage unless the ring is chosen, and only where it can be drawn (WebGL2, show.ts canStage) —
      // without it the flat ring is the show. With it the flat ring is out of sight from the first paint, the ground plain until
      // the stage fades in (the stage mounts over it, from main.js)
      var sh = showAsked() || window.localStorage.getItem(SHOW_KEY);
      if (sh !== "classic" && sh !== "flat" && typeof WebGL2RenderingContext !== "undefined") h.dataset.show = "stage";
      if (window.localStorage.getItem(MOTION_KEY) === "0") h.dataset.motion = "off"; // the animations off under the gear: nothing moves from the first paint
      // the tv's panel as the viewer left it (panelui.ts): the sections folded, the lists' heights, the panel's width — on the
      // html element, so the panel comes up that way and a channel change (which swaps the panel's markup) keeps it
      var pl = JSON.parse(window.localStorage.getItem(PANEL_KEY) || "null");
      if (pl && typeof pl === "object") {
        if (Array.isArray(pl.fold) && pl.fold.length) h.dataset.fold = pl.fold.join(" ");
        if (typeof pl.width === "number") h.style.setProperty("--panel-w", pl.width + "px");
        if (pl.hidden === true) h.dataset.panelHidden = "1";
        if (pl.sizes && typeof pl.sizes === "object") {
          var any = false;
          for (var k in pl.sizes)
            if (typeof pl.sizes[k] === "number") {
              h.style.setProperty("--w-" + k, String(pl.sizes[k]));
              any = true;
            }
          if (any) h.dataset.sized = "1";
        }
      }
    }
  } catch (e) {
    /* storage may be off; the defaults stand */
  }
  // the theme (#437): light or dark from the first paint, so the page never flashes the other one — the viewer's choice, else
  // the device's (dark unless it asks for light); look.ts keeps it in step after. A broadcast (the stream's box) is always dark.
  var theme = "system";
  try {
    theme = (window.localStorage && window.localStorage.getItem(THEME_KEY)) || "system";
  } catch (e) {
    /* storage off: the device's */
  }
  var isBroadcast = onBroadcast();
  if (isBroadcast) theme = "dark";
  else if (theme !== "light" && theme !== "dark")
    theme = window.matchMedia && window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
  h.dataset.theme = theme;

  // the broadcast (the stream's box) is the URL's say and nothing else: it once set a cookie and a storage flag that
  // kept the mode for a year, and one visit with the flag turned a person's site into the stream (2026-10-17). What
  // was left behind is cleared here on every load.
  try {
    if (window.localStorage && window.localStorage.getItem("basho.broadcast") !== null)
      window.localStorage.removeItem("basho.broadcast");
    if (/(?:^|;\s*)basho_broadcast=/.test(document.cookie))
      document.cookie =
        "basho_broadcast=; Max-Age=0; Path=/; SameSite=Lax" + (location.protocol === "https:" ? "; Secure" : "");
  } catch (e) {}

  if (isBroadcast) {
    document.addEventListener("DOMContentLoaded", function () {
      var b = document.querySelector(".consent-banner");
      if (b) b.remove();
    });
  }

  window.dataLayer = window.dataLayer || [];
  // gtag's own shape: the tag manager reads the arguments object itself, so it is pushed as it is; the parameter only names the call
  function gtag(..._args: unknown[]) {
    (window.dataLayer = window.dataLayer || []).push(arguments);
  }
  window.gtag = gtag;

  var id = isBroadcast ? null : h.dataset.gtm;
  if (id) {
    var hasGranted = /(?:^|;\s*)basho_consent=granted\b/.test(document.cookie);
    gtag("consent", "default", {
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      analytics_storage: hasGranted ? "granted" : "denied",
    });

    window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
    var j = document.createElement("script");
    j.async = true;
    j.src = "https://www.googletagmanager.com/gtm.js?id=" + encodeURIComponent(id);
    document.head.appendChild(j);
  }
})();
